import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { data } from "./ProjectArray";
import {
  FaExternalLinkAlt,
  FaPlay,
  FaReact,
  FaVuejs,
  FaHtml5,
  FaNodeJs,
} from "react-icons/fa";
import { SiFirebase, SiGatsby, SiGraphql } from "react-icons/si";

const categoryIcons = {
  React: <FaReact />,
  "Vue/Pinia": <FaVuejs />,
  "HTML/SCSS": <FaHtml5 />,
  Firebase: <SiFirebase />,
  gatsby: <SiGatsby />,
  "Mern Stack": <FaNodeJs />,
  GraphQL: <SiGraphql />,
};

const Projects = () => {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    document.body.style.overflow = activeVideo ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeVideo]);

  const categories = [
    "all",
    ...new Set(
      data.map((project) => {
        const cat = project.category.split(",")[0].trim();
        return cat;
      }),
    ),
  ];

  const normalizedQuery = search.trim().toLowerCase();
  const matchesQuery = (project) => {
    if (!normalizedQuery) return true;
    const haystack = [
      project.name,
      project.description,
      project.category,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  };

  const matchesCategory = (project) =>
    filter === "all"
      ? true
      : project.category.toLowerCase().includes(filter.toLowerCase());

  const filteredProjects = data.filter(
    (project) => matchesCategory(project) && matchesQuery(project),
  );

  const openVideo = (project) => setActiveVideo(project);
  const closeVideo = () => setActiveVideo(null);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <section className="projects section projects-modern" id="projects">
        <div className="section-title">
          <h2>Featured Projects</h2>
          <div className="underline"></div>
          <p className="projects-description">
            A showcase of modern web applications built with cutting-edge
            technologies and best practices.
          </p>
        </div>

        <div className="projects-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`filter-btn ${filter === cat ? "active" : ""}`}
              onClick={() => setFilter(cat)}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
          <div className="projects-search">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects"
              aria-label="Search projects"
              className="projects-search-input"
            />
          </div>
        </div>

        <div className="projects-grid">
          {filteredProjects.map((project, index) => {
            const { name, img, description, link, category, video } = project;
            const icon = categoryIcons[category.split(",")[0].trim()] || (
              <FaReact />
            );
            const hasVideo = Boolean(video);

            return (
              <motion.article
                key={name}
                className={`project-card-modern ${
                  hasVideo ? "project-has-video" : ""
                }`}
                onClick={() => hasVideo && openVideo(project)}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="project-card-inner">
                  <div className="project-image-wrapper">
                    <img
                      src={img}
                      alt={name}
                      className="project-image-modern"
                    />
                    <div className="project-overlay">
                      {hasVideo ? (
                        <button
                          type="button"
                          className="project-link-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openVideo(project);
                          }}
                        >
                          <FaPlay />
                          <span>Play Demo</span>
                        </button>
                      ) : (
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-link-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaExternalLinkAlt />
                          <span>View Project</span>
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="project-card-content">
                    <div className="project-tech-icon">{icon}</div>

                    <h3 className="project-title-modern">{name}</h3>

                    <p className="project-description-modern">{description}</p>

                    <div className="project-tech-stack">
                      {category.split(",").map((tech, idx) => (
                        <span key={idx} className="tech-badge">
                          {tech.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
        {filteredProjects.length === 0 && (
          <p className="projects-empty">No projects match your search.</p>
        )}

        {activeVideo && (
          <div className="video-modal-overlay" onClick={closeVideo}>
            <div
              className="video-modal"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <button
                className="video-close-btn"
                onClick={closeVideo}
                aria-label="Close video"
              >
                &times;
              </button>
              <h3 className="video-title">{activeVideo.name}</h3>
              <video
                src={activeVideo.video}
                controls
                autoPlay
                playsInline
                preload="none"
                poster={activeVideo.poster || activeVideo.img}
                className="video-player"
              />
            </div>
          </div>
        )}
      </section>
    </motion.div>
  );
};

export default Projects;
